'use client'
import { motion } from 'framer-motion'
import Icon from './Icon'
import { useLang } from '../context/LangContext'
import { t, ctaSection as ctaT, toLocalDigits } from '../lib/translations'

interface CTASectionProps {
  id?: string
  secondaryHref?: string
  secondaryOnClick?: () => void
}

export default function CTASection({
  id,
  secondaryHref = '#cta',
  secondaryOnClick,
}: CTASectionProps) {
  const lang = useLang()

  return (
    <section id={id} className="relative py-24 sm:py-32">
      <div className="absolute inset-0 dot-pattern pointer-events-none" />

      <div className="relative max-w-4xl mx-auto px-8 sm:px-8 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative rounded-xl p-10 sm:p-16 overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--brand-950)]/80 via-[var(--brand-950)]/60 to-[#07070d] rounded-xl border border-[rgb(var(--brand-rgb)/0.2)]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[rgb(var(--brand-rgb)/0.1)] via-transparent to-[rgb(var(--brand-rgb)/0.1)] rounded-xl" />

          <div className="relative">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-center">
              {t(lang, ctaT.heading1)}{' '}
              {(() => {
                const text = t(lang, ctaT.heading2)
                const idx = text.indexOf('مشاوره')
                if (idx === -1) return text
                return (
                  <>
                    <span className="gradient-text">{'مشاوره'}</span>
                    <span>{text.slice(idx + 'مشاوره'.length)}</span>
                  </>
                )
              })()}
            </h2>
            <p className="mt-5 text-lg text-[#94a3b8] max-w-xl mx-auto">
              {t(lang, ctaT.description)}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+989920115977"
                className="w-full sm:w-auto text-center group relative px-8 py-3.5 rounded-xl bg-gradient-to-r from-[var(--brand-600)] to-[var(--brand-500)] font-semibold text-sm hover:from-[var(--brand-500)] hover:to-[var(--brand-300)] transition-all duration-200 shadow-2xl shadow-[rgb(var(--brand-rgb)/0.25)] glow btn-lightning"
              >
                <span className="relative z-10 flex items-center justify-center gap-2" dir="ltr">
                  <Icon name="phone" className="w-4 h-4" />
                  {toLocalDigits('+98 992 011 5977', lang)}
                </span>
              </a>
              {secondaryOnClick ? (
                <button
                  onClick={secondaryOnClick}
                  className="w-full sm:w-auto text-center px-8 py-3.5 min-w-[220px] rounded-xl glass glass-hover font-medium text-sm text-[#e2e8f0] transition-all duration-300 cursor-pointer"
                >
                  {t(lang, ctaT.submitRequest)}
                </button>
              ) : (
                <a
                  href={secondaryHref}
                  className="w-full sm:w-auto text-center px-8 py-3.5 min-w-[220px] rounded-xl glass glass-hover font-medium text-sm text-[#e2e8f0] transition-all duration-300 cursor-pointer"
                >
                  {t(lang, ctaT.submitRequest)}
                </a>
              )}
            </div>
            <p className="mt-5 text-xs text-[#475569]">
              {t(lang, ctaT.supportNote)}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
